import type { ZyDocumentAttributes } from '@/components/tiptap-editor/tiptap-extension/zydocument-extension';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle as DialogHeading } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { getEditorVersionFilter, getZyVersions, setDocAttributes, type SelectedZyVersionFilter } from '@/lib/zy-tiptap-utils';
import type { Editor } from '@tiptap/react';
import { useEditorState } from '@tiptap/react';
import { useCallback, useMemo, useState } from 'react';

const TITLE_KEY_DEFAULT = 'default';

/**
 * Determines the effective main title key for the document based on the selected ZyVersion filter.
 *
 * @param versionFilter - The active ZyVersion filter, which can be null, a string, or an array of strings.
 * @returns The key to use for the document's main section title.
 */
function effectiveMainTitleKey(versionFilter: SelectedZyVersionFilter): string {
    if (versionFilter == null) {
        return TITLE_KEY_DEFAULT;
    }
    if (typeof versionFilter === 'string') {
        return versionFilter;
    }
    if (versionFilter.length === 1) {
        return versionFilter[0]!;
    }
    return TITLE_KEY_DEFAULT;
}

type DocTitleState = {
    title: ZyDocumentAttributes['title'];
    versionFilter: SelectedZyVersionFilter;
} | null;

export type ZyDocumentTitleProps = {
    editor: Editor | null;
    className?: string;
};

function titleKeysForEditor(editor: Editor): string[] {
    return [TITLE_KEY_DEFAULT, ...getZyVersions(editor)];
}

export function ZyDocumentTitle({ editor, className }: ZyDocumentTitleProps) {
    const [titleModalOpen, setTitleModalOpen] = useState(false);
    const [titleDrafts, setTitleDrafts] = useState<Record<string, string>>({});
    const [titleModalKeys, setTitleModalKeys] = useState<string[]>([TITLE_KEY_DEFAULT]);

    const state = useEditorState({
        editor,
        selector: (ctx): DocTitleState =>
            ctx.editor
                ? {
                      title: { ...ctx.editor.state.doc.attrs.title },
                      versionFilter: getEditorVersionFilter(ctx.editor),
                  }
                : null,
    });

    const { mainSectionTitle, activeKey } = useMemo(() => {
        if (!state) {
            return { mainSectionTitle: '', activeKey: TITLE_KEY_DEFAULT };
        }
        const title = state.title ?? {};
        const activeKey = effectiveMainTitleKey(state.versionFilter);
        return {
            mainSectionTitle: title[activeKey] ?? '',
            activeKey,
        };
    }, [state]);

    const openTitleModal = useCallback(() => {
        if (!editor) return;
        const attrs = editor.state.doc.attrs;
        const title = attrs.title ?? {};
        const keys = titleKeysForEditor(editor);
        const drafts: Record<string, string> = {};
        for (const key of keys) {
            drafts[key] = title[key] ?? '';
        }
        setTitleModalKeys(keys);
        setTitleDrafts(drafts);
        setTitleModalOpen(true);
    }, [editor]);

    const closeTitleModal = useCallback(() => {
        setTitleModalOpen(false);
    }, []);

    const handleSaveTitles = useCallback(() => {
        if (!editor) return;
        const attrs = editor.state.doc.attrs;
        const prevTitle = attrs.title ?? {};
        const keys = titleModalKeys;
        const next = { ...prevTitle };

        for (const key of keys) {
            const draft = titleDrafts[key] ?? '';
            if (key === TITLE_KEY_DEFAULT) {
                next[TITLE_KEY_DEFAULT] = draft;
            } else if (draft.trim() === '') {
                delete next[key];
            } else {
                next[key] = draft;
            }
        }

        const changed = keys.some(key => (prevTitle[key] ?? '') !== (next[key] ?? ''));

        if (changed) {
            setDocAttributes(editor, { title: next });
        }
        setTitleModalOpen(false);
    }, [editor, titleDrafts, titleModalKeys]);

    const setDraftForKey = useCallback((key: string, value: string) => {
        setTitleDrafts(d => ({ ...d, [key]: value }));
    }, []);

    return (
        <div className={cn('flex min-w-0 flex-1 flex-col gap-1 pr-2', className)}>
            <div className="flex min-w-0 items-center gap-2">
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="shrink-0"
                    onClick={openTitleModal}
                    disabled={!editor}
                    aria-label={titleModalKeys.length > 1 ? 'Edit section titles' : 'Edit section title'}
                >
                    Edit title
                </Button>
                <span
                    className={cn(
                        'min-w-0 flex-1 truncate text-2xl font-bold',
                        mainSectionTitle ? 'text-foreground' : 'text-muted-foreground',
                    )}
                >
                    {mainSectionTitle || (activeKey === TITLE_KEY_DEFAULT ? 'Section title' : `Title for ${activeKey}`)}
                </span>
            </div>

            <Dialog
                open={titleModalOpen}
                onOpenChange={open => {
                    if (!open) {
                        closeTitleModal();
                    }
                }}
            >
                <DialogContent className="max-h-[min(32rem,85vh)] max-w-lg gap-0 overflow-hidden p-0 sm:max-w-lg">
                    <div className="max-h-[min(32rem,85vh)] overflow-y-auto p-6 pb-0">
                        <DialogHeader>
                            <DialogHeading>{titleModalKeys.length > 1 ? 'Edit section titles' : 'Edit section title'}</DialogHeading>
                        </DialogHeader>
                        <ul className="flex flex-col gap-4 py-4">
                            {titleModalKeys.map((key, index) => {
                                const label = key === TITLE_KEY_DEFAULT ? 'Default' : key;
                                const fieldId = `zy-doc-title-modal-${index}`;
                                return (
                                    <li
                                        key={key}
                                        className="flex flex-col gap-1.5"
                                    >
                                        <label
                                            htmlFor={fieldId}
                                            className="text-muted-foreground text-xs font-medium"
                                        >
                                            {label}
                                        </label>
                                        <Input
                                            id={fieldId}
                                            value={titleDrafts[key] ?? ''}
                                            onChange={e => setDraftForKey(key, e.target.value)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleSaveTitles();
                                                }
                                            }}
                                            placeholder={key === TITLE_KEY_DEFAULT ? 'Section title' : `Title for ${key}`}
                                            className="min-w-0"
                                        />
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                    <DialogFooter className="border-border gap-2 border-t p-6 sm:gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={closeTitleModal}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSaveTitles}
                        >
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default ZyDocumentTitle;
